import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { MessageCircle, Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'react-toastify';
import { useAppSelector } from '@/store/hooks';
import { hasWhatsAppModuleAccess } from '@/features/auth/moduleFeatures';
import { selectCurrentUser } from '@/features/auth/authSlice';
import { chatbotAdminApi } from '@/features/chatbot/api/chatbotApi';
import { useConfirm } from '@/contexts/confirmContext';
import type { WhatsAppChannel, WhatsAppProvider } from '@workspace/types';
import { whatsappAdminApi } from '../api/whatsappApi';
import { WhatsAppChannelPanel } from '../components/WhatsAppChannelPanel';
import { WhatsAppChannelDetailDialog } from '../components/WhatsAppChannelDetailDialog';

type BotLabelMap = Record<string, string>;

type InstanceBotOption = {
  instanceBotId: string;
  label: string;
};

function formatDate(value: string): string {
  return new Date(value).toLocaleString();
}

function providerLabel(provider: WhatsAppProvider): string {
  if (provider === 'TWILIO') return 'Twilio';
  if (provider === 'WAHA') return 'WhatsApp (número propio / WAHA)';
  if (provider === 'EVOHUB') return 'Evo Hub (evoapicloud)';
  return '360dialog';
}

export function WhatsAppChannelsPage() {
  const currentUser = useAppSelector(selectCurrentUser);
  const { confirm } = useConfirm();
  const [searchParams, setSearchParams] = useSearchParams();
  const isSuperAdmin = currentUser?.role === 'SUPERADMIN';
  const hasWhatsApp = hasWhatsAppModuleAccess(currentUser);

  const [channels, setChannels] = useState<WhatsAppChannel[]>([]);
  const [botLabels, setBotLabels] = useState<BotLabelMap>({});
  const [instanceBotOptions, setInstanceBotOptions] = useState<InstanceBotOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [createBotId, setCreateBotId] = useState('');
  const [detailChannel, setDetailChannel] = useState<WhatsAppChannel | null>(null);
  const botParamHandled = useRef(false);

  const loadData = useCallback(async () => {
    if (!hasWhatsApp) {
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const tenantId = isSuperAdmin ? undefined : currentUser?.tenantId || undefined;
      const [channelList, instances] = await Promise.all([
        whatsappAdminApi.listChannels(),
        chatbotAdminApi.listInstances(tenantId),
      ]);
      setChannels(channelList);

      const labels: BotLabelMap = {};
      const usedIds = new Set(channelList.map(channel => channel.instanceBotId));
      const available: InstanceBotOption[] = [];

      for (const instance of instances) {
        const bots = await chatbotAdminApi.listBots(instance.id);
        for (const bot of bots) {
          if (!bot.id) {
            continue;
          }
          const label = `${instance.name} / ${bot.name}`;
          labels[bot.id] = label;
          if (!usedIds.has(bot.id)) {
            available.push({ instanceBotId: bot.id, label });
          }
        }
      }

      setBotLabels(labels);
      setInstanceBotOptions(available);
    } catch (error) {
      toast.error((error as Error).message || 'Error al cargar canales WhatsApp');
    } finally {
      setLoading(false);
    }
  }, [hasWhatsApp, isSuperAdmin, currentUser?.tenantId]);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  useEffect(() => {
    const botId = searchParams.get('bot');
    if (!botId || loading || botParamHandled.current) {
      return;
    }
    botParamHandled.current = true;

    const existing = channels.find(channel => channel.instanceBotId === botId);
    if (existing) {
      setDetailChannel(existing);
    } else if (botLabels[botId] || instanceBotOptions.some(option => option.instanceBotId === botId)) {
      setCreateBotId(botId);
      setCreateOpen(true);
    }

    setSearchParams({}, { replace: true });
  }, [loading, channels, botLabels, instanceBotOptions, searchParams, setSearchParams]);

  const sortedChannels = useMemo(
    () => [...channels].sort((a, b) => b.updatedAt.localeCompare(a.updatedAt)),
    [channels]
  );

  const createBotLabel = useMemo(() => {
    if (!createBotId) {
      return '';
    }
    return (
      instanceBotOptions.find(option => option.instanceBotId === createBotId)?.label ??
      botLabels[createBotId] ??
      createBotId
    );
  }, [createBotId, instanceBotOptions, botLabels]);

  const handleDelete = async (channel: WhatsAppChannel) => {
    const confirmed = await confirm({
      title: 'Eliminar canal',
      message: `¿Eliminar el canal WhatsApp (${channel.displayPhoneNumber})? Esta acción no se puede deshacer.`,
      confirmText: 'Eliminar',
      cancelText: 'Cancelar',
      variant: 'danger',
    });
    if (!confirmed) {
      return;
    }
    try {
      await whatsappAdminApi.deleteChannel(channel.id);
      if (detailChannel?.id === channel.id) {
        setDetailChannel(null);
      }
      await loadData();
      toast.success('Canal eliminado');
    } catch (error) {
      toast.error((error as Error).message || 'Error al eliminar canal');
    }
  };

  const handleCreateOpenChange = (open: boolean) => {
    setCreateOpen(open);
    if (!open) {
      setCreateBotId('');
    }
  };

  if (!hasWhatsApp) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className='flex items-center gap-2'>
            <MessageCircle className='h-5 w-5' />
            WhatsApp
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className='text-sm text-muted-foreground'>
            La integración WhatsApp no está habilitada para tu tenant. Activá la feature{' '}
            Habilitá al menos un proveedor WhatsApp (360dialog, Twilio, Evo Hub o WAHA) en la
            configuración del tenant.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className='space-y-6'>
      <div className='flex flex-wrap items-center justify-between gap-3'>
        <div>
          <h1 className='text-2xl font-semibold flex items-center gap-2'>
            <MessageCircle className='h-6 w-6' />
            Canales WhatsApp
          </h1>
          <p className='text-sm text-muted-foreground mt-1'>
            Configurá proveedores 360dialog y Twilio por bot, whitelist de teléfonos y plantillas.
          </p>
        </div>
        <Button
          type='button'
          onClick={() => setCreateOpen(true)}
          disabled={instanceBotOptions.length === 0 && channels.length > 0}
        >
          <Plus className='h-4 w-4 mr-2' />
          Nuevo canal
        </Button>
      </div>

      <Card>
        <CardContent className='pt-6'>
          {loading ? (
            <p className='text-sm text-muted-foreground'>Cargando canales…</p>
          ) : sortedChannels.length === 0 ? (
            <p className='text-sm text-muted-foreground'>
              No hay canales WhatsApp. Creá uno y vinculalo a un bot de chat.
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Número</TableHead>
                  <TableHead>Bot / instancia</TableHead>
                  <TableHead>Proveedor</TableHead>
                  <TableHead>Estado</TableHead>
                  <TableHead>Actualizado</TableHead>
                  <TableHead className='text-right'>Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedChannels.map(channel => (
                  <TableRow key={channel.id}>
                    <TableCell className='font-medium font-mono text-sm'>
                      {channel.displayPhoneNumber}
                    </TableCell>
                    <TableCell className='text-sm'>
                      {botLabels[channel.instanceBotId] ?? channel.instanceBotId}
                    </TableCell>
                    <TableCell>
                      <Badge variant='outline'>{providerLabel(channel.provider)}</Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant={channel.status === 'ACTIVE' ? 'secondary' : 'outline'}>
                        {channel.status}
                      </Badge>
                    </TableCell>
                    <TableCell className='text-sm text-muted-foreground'>
                      {formatDate(channel.updatedAt)}
                    </TableCell>
                    <TableCell className='text-right space-x-2'>
                      <Button
                        type='button'
                        size='sm'
                        variant='outline'
                        onClick={() => setDetailChannel(channel)}
                      >
                        Configurar
                      </Button>
                      <Button
                        type='button'
                        size='sm'
                        variant='ghost'
                        onClick={() => void handleDelete(channel)}
                        title='Eliminar'
                      >
                        <Trash2 className='h-4 w-4 text-red-500' />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={createOpen} onOpenChange={handleCreateOpenChange}>
        <DialogContent className='max-w-3xl max-h-[90vh] overflow-y-auto'>
          <DialogHeader>
            <DialogTitle>Nuevo canal WhatsApp</DialogTitle>
          </DialogHeader>
          {instanceBotOptions.length === 0 ? (
            <p className='text-sm text-muted-foreground'>
              Todos los bots ya tienen un canal WhatsApp asignado.
            </p>
          ) : (
            <div className='space-y-4'>
              <div className='space-y-2'>
                <Label htmlFor='whatsapp-create-bot'>Bot / instancia</Label>
                <select
                  id='whatsapp-create-bot'
                  className='flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm'
                  value={createBotId}
                  onChange={event => setCreateBotId(event.target.value)}
                >
                  <option value=''>Seleccionar bot…</option>
                  {instanceBotOptions.map(option => (
                    <option key={option.instanceBotId} value={option.instanceBotId}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
              {createBotId ? (
                <WhatsAppChannelPanel
                  instanceBotId={createBotId}
                  botName={createBotLabel}
                  onChannelChange={() => {
                    void loadData();
                    handleCreateOpenChange(false);
                  }}
                />
              ) : (
                <p className='text-sm text-muted-foreground'>
                  Elegí el bot al que querés vincular el canal.
                </p>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <WhatsAppChannelDetailDialog
        channel={detailChannel}
        botLabel={
          detailChannel
            ? (botLabels[detailChannel.instanceBotId] ?? detailChannel.instanceBotId)
            : ''
        }
        open={Boolean(detailChannel)}
        onOpenChange={open => !open && setDetailChannel(null)}
        onChannelUpdated={() => void loadData()}
      />
    </div>
  );
}
